// GroupedHrZoneCards.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, Typography, Box } from '@mui/material';
import { PieChart } from 'react-minimal-pie-chart';

const formatBpmLabel = (sub, subIndex, zoneSubCount, classicZones) => {
    if (subIndex === 0 && Number(sub.min) === 0) return `< ${sub.max} bpm`;
    if (
        subIndex === zoneSubCount - 1 &&
        Number(sub.max) >= Number(classicZones[classicZones.length - 1].max)
    ) {
        return `> ${sub.min} bpm`;
    }
    return `${sub.min} - ${sub.max} bpm`;
};

const GroupedHrZoneCards = ({ groupedZones = [], classicZones = [] }) => {
    if (!groupedZones.length || !classicZones.length) return null;

    const zonesWithSubs = groupedZones.map((group, groupIndex) => {
        const subZones = classicZones.slice(groupIndex * 2, groupIndex * 2 + 2);
        return { ...group, subZones };
    });

    return (
        <Grid container spacing={2}>
            {zonesWithSubs.map((zone, idx) => {
                const zoneValue = Number(zone.value) || 0;

                const pieSegments = (zone.subZones || []).map((s) => ({
                    title: s.label,
                    value: Number(s.value) || 0,
                    color: s.color || '#999'
                }));

                const sumSubValues = pieSegments.reduce(
                    (acc, p) => acc + (Number(p.value) || 0),
                    0
                );
                const remainder = Math.max(0, 100 - sumSubValues);

                if (remainder > 0) {
                    pieSegments.push({
                        title: 'remainder',
                        value: remainder,
                        color: '#eee'
                    });
                }

                const circleSize = 200;

                return (
                    <Grid item xs={12} sm={4} key={idx}>
                        <Card sx={{ p: 2, textAlign: 'center' }}>
                            {/* Circle chart */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: circleSize,
                                    height: circleSize,
                                    mx: 'auto',
                                    mb: 2
                                }}
                            >
                                <PieChart
                                    data={pieSegments}
                                    startAngle={-90}
                                    lineWidth={25}
                                    rounded={false}
                                    style={{ height: '100%' }}
                                />

                                {/* Center text */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center',
                                        width: circleSize * 0.7
                                    }}
                                >
                                    <Typography
                                        variant="h2"
                                        sx={{ fontWeight: 'bold', color: zone.color }}
                                    >
                                        {zone.label}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {Math.round(zoneValue)}% • {zone.time}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Legend */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap'
                                }}
                            >
                                {zone.subZones.map((sub, subIndex) => {
                                    const bpmLabel = formatBpmLabel(
                                        sub,
                                        subIndex,
                                        (zone.subZones || []).length,
                                        classicZones
                                    );
                                    return (
                                        <Box
                                            key={subIndex}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                minWidth: 100
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    backgroundColor: sub.color || '#999',
                                                    borderRadius: 1
                                                }}
                                            />
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {sub.label}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    {sub.time} • {bpmLabel}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

GroupedHrZoneCards.propTypes = {
    groupedZones: PropTypes.array.isRequired,
    classicZones: PropTypes.array.isRequired
};

export default GroupedHrZoneCards;
